import { useCallback, useState } from 'react'
import { useSessionStorage } from 'react-use'
import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import type { GlobalSearchResponse } from '@ztechapps/common.contract'
import { useDeepCompareEffect } from '@ztechapps/common.hook'

const STORAGE_KEY = 'global-search-query'

export type GlobalSearchVariables = {
  filter: {
    query: string
  }
}

export type GlobalSearchData = {
  globalSearch: GlobalSearchResponse
}

const GLOBAL_SEARCH_QUERY = gql`
  query GlobalSearch($filter: JSON) {
    globalSearch(filter: $filter) {
      groups {
        module
        count
        results {
          _id
          code
          externalId
          name
          summary
          module
          score
        }
      }
      totalResults
      executionTimeMs
    }
  }
`

export type UseGlobalSearchResult = {
  searchQuery: string
  setSearchQuery: (query: string) => void
  handleSearch: () => void
  results: GlobalSearchResponse | undefined
  loading: boolean
  error: any
  canSearch: boolean
  clearSearch: () => void
}

export const useGlobalSearch = (): UseGlobalSearchResult => {
  const [searchQuery, setSearchQuery] = useSessionStorage<string>(STORAGE_KEY, '')

  const [executeSearch, { data, loading, error }] = useLazyQuery<GlobalSearchData, GlobalSearchVariables>(
    GLOBAL_SEARCH_QUERY
  )

  const [results, setResults] = useState<GlobalSearchResponse | undefined>(data?.globalSearch)

  useDeepCompareEffect(() => {
    setResults(data?.globalSearch)
  }, [data?.globalSearch])

  const handleSearch = useCallback(() => {
    if (searchQuery && searchQuery.length >= 3) {
      executeSearch({
        variables: {
          filter: {
            query: searchQuery,
          },
        },
      })
    } else {
      //clear the data
      setSearchQuery('')
      setResults(undefined)
    }
  }, [searchQuery, executeSearch, setSearchQuery])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
  }, [setSearchQuery])

  return {
    searchQuery: searchQuery || '',
    setSearchQuery,
    handleSearch,
    results,
    loading,
    error,
    canSearch: (searchQuery?.length ?? 0) >= 3,
    clearSearch,
  }
}
